import { useRef, useState } from "react";
import { Download, FileText, FolderOpen, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/common/Page";
import { EmptyState, ErrorState, LoadingState, Spinner } from "@/components/common/States";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { useFetch } from "@/hooks/useFetch";
import { api, API, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";

const ACCEPT = ".pdf,.doc,.docx,.ppt,.pptx,.xlsx,.txt,.png,.jpg,.jpeg,.webp";
const size = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export const MaterialList = ({ items, onDelete }) => (
  <div className="divide-y rounded-xl border bg-card" data-testid="materials-list">
    {items.map((m) => (
      <div key={m.id} className="flex items-center gap-3 px-4 py-3 text-sm" data-testid={`material-row-${m.id}`}>
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><FileText className="h-4 w-4" /></div>
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold">{m.title}</p>
          <p className="truncate text-xs text-muted-foreground">{m.description ? `${m.description} · ` : ""}{m.original_filename} · {size(m.size)} · {fmtDate(m.created_at.slice(0, 10))}</p>
        </div>
        <a href={`${API}/materials/${m.id}/download`} className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-primary" aria-label="Baixar" data-testid={`material-download-${m.id}`}><Download className="h-4 w-4" /></a>
        {onDelete && <button onClick={() => onDelete(m)} className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-destructive" aria-label="Excluir" data-testid={`material-delete-${m.id}`}><Trash2 className="h-4 w-4" /></button>}
      </div>
    ))}
  </div>
);

export const MaterialsPanel = ({ studentId }) => {
  const { data, loading, error, reload } = useFetch(`/students/${studentId}/materials`);
  const fileRef = useRef();
  const [form, setForm] = useState({ title: "", description: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState(null);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Selecione um arquivo");
    const body = new FormData();
    body.append("title", form.title);
    body.append("description", form.description);
    body.append("file", file);
    setBusy(true);
    try {
      await api.post(`/students/${studentId}/materials`, body);
      toast.success("Material enviado. Ele já aparece no portal do aluno.");
      setForm({ title: "", description: "" });
      setFile(null);
      fileRef.current.value = "";
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/materials/${removing.id}`);
      toast.success("Material removido");
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="space-y-4">
      <form onSubmit={upload} className="grid gap-3 rounded-xl border bg-card p-5 sm:grid-cols-2" data-testid="material-upload-form">
        <Field label="Título *"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex.: Apostila de equações" required minLength={2} data-testid="material-title-input" /></Field>
        <Field label="Descrição"><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Ex.: Exercícios 1 a 20" data-testid="material-description-input" /></Field>
        <Field label="Arquivo *" hint="PDF, Word, PowerPoint, Excel, TXT ou imagem · até 20 MB" className="sm:col-span-2">
          <Input ref={fileRef} type="file" accept={ACCEPT} onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="material-file-input" />
        </Field>
        <div className="sm:col-span-2"><Button type="submit" disabled={busy} data-testid="material-upload-btn">{busy ? <Spinner className="mr-2" /> : <Upload className="mr-2 h-4 w-4" />}Enviar material</Button></div>
      </form>
      {error ? <ErrorState error={error} onRetry={reload} /> : loading && !data ? <LoadingState rows={2} /> : data.length === 0
        ? <EmptyState icon={FolderOpen} title="Nenhum material ainda" description="Envie apostilas e exercícios. O aluno e o responsável poderão baixar pelo portal." testId="materials-empty" />
        : <MaterialList items={data} onDelete={setRemoving} />}
      <ConfirmDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)} title="Excluir material?" destructive confirmLabel="Excluir"
        description="O material deixará de aparecer no portal do aluno." onConfirm={remove} />
    </div>
  );
};
